import config from 'config';

import { ACCOUNT_STATUS_CODE } from 'utilities/constants';

/**
 * The source types bulk upload reads a CSV for. Which one an upload uses follows
 * from its actions — see `csvSpecForActions`.
 */
export const SOURCE_TYPE = Object.freeze({
  ACCOUNT_ID: 'ACCOUNT_ID',
  ECS_CUSTOMER_ID: 'ECS_CUSTOMER_ID',
});

/** Mirrors `bulk.upload.max-row-count` in fraud-rules-service, which enforces it. */
export const MAX_ROWS = 1000;

/**
 * Mirrors `bulk.upload.max-size-bytes`. Only used to refuse a file before reading
 * it into memory — the limit that actually decides is the one createBulkUpload
 * returns, which is per-environment and may not match this.
 */
export const MAX_SIZE_BYTES = 10 * 1024 * 1024;

export const ACTION_TYPE = Object.freeze({
  ACCOUNT_UPDATE: 'ACCOUNT_UPDATE',
  SOCURE: 'SOCURE',
  ZENDESK: 'ZENDESK',
  SECURITY_REPORT: 'SECURITY_REPORT',
  CUSTOMER_INFO_UPDATE: 'CUSTOMER_INFO_UPDATE',
});

/** Every action as an agent reads it, including the ones recorded in history. */
export const ACTION_LABEL = Object.freeze({
  [ACTION_TYPE.ACCOUNT_UPDATE]: 'Apply Account status',
  [ACTION_TYPE.SOCURE]: 'Create DocV link',
  [ACTION_TYPE.ZENDESK]: 'Create Zendesk ticket',
  [ACTION_TYPE.SECURITY_REPORT]: 'Add Account Security',
  [ACTION_TYPE.CUSTOMER_INFO_UPDATE]: 'Update customer info',
});

/**
 * The actions an agent picks from. The security report is not one of them: it is
 * only valid beside a CL V9 closure, so it is a checkbox on that closure instead.
 */
export const ACTION_OPTIONS = Object.freeze(
  [
    ACTION_TYPE.ACCOUNT_UPDATE,
    ACTION_TYPE.SOCURE,
    ACTION_TYPE.ZENDESK,
    ACTION_TYPE.CUSTOMER_INFO_UPDATE,
  ].map((value) => ({ value, label: ACTION_LABEL[value] })),
);

/**
 * fraud-rules-service refuses these beside any other action: a customer info
 * update is keyed by customer, the rest by account, so one file cannot be both.
 */
export const EXCLUSIVE_ACTIONS = Object.freeze([
  ACTION_TYPE.CUSTOMER_INFO_UPDATE,
]);

/**
 * Format checks only; whether a value is acceptable is the customer service's
 * call. A new attribute is one more entry here, once `BulkCustomerAttribute` has it.
 */
export const CUSTOMER_ATTRIBUTE_OPTIONS = Object.freeze([
  {
    value: 'BIRTH_DATE',
    label: 'Date of birth',
    csvColumn: 'birth_date',
    valuePattern: /^\d{4}-\d{2}-\d{2}$/,
    formatHint: 'yyyy-MM-dd',
  },
]);

export const CUSTOMER_ATTRIBUTE_LABEL = Object.freeze(
  Object.fromEntries(
    CUSTOMER_ATTRIBUTE_OPTIONS.map(({ value, label }) => [value, label]),
  ),
);

const ACCOUNT_ID_COLUMN = 'account_id';
const CUSTOMER_ID_COLUMN = 'customer_id';

/**
 * The CSV file an upload's actions call for. `requiredColumns` starts with
 * `idColumn`; every value in them must be non-blank and match the column's
 * `valuePattern`, if it has one.
 */
export const csvSpecForActions = (actions = []) => {
  const customerInfoUpdateAction = actions.find(
    ({ type }) => type === ACTION_TYPE.CUSTOMER_INFO_UPDATE,
  );

  if (!customerInfoUpdateAction) {
    // An account may legitimately appear on several rows, and its id is sent
    // exactly as written, so the only check is that it is there.
    return {
      sourceType: SOURCE_TYPE.ACCOUNT_ID,
      idColumn: ACCOUNT_ID_COLUMN,
      recordType: 'account',
      requireUniqueIds: false,
      extraHint:
        'Ids are sent exactly as written — leading zeros are significant.',
      requiredColumns: [{ name: ACCOUNT_ID_COLUMN }],
    };
  }

  const selectedAttributes = customerInfoUpdateAction.attributes ?? [];

  return {
    sourceType: SOURCE_TYPE.ECS_CUSTOMER_ID,
    idColumn: CUSTOMER_ID_COLUMN,
    recordType: 'customer',
    requireUniqueIds: true,
    requiredColumns: [
      {
        name: CUSTOMER_ID_COLUMN,
        valuePattern: /^\d+$/,
        formatHint: 'digits only',
      },
      ...CUSTOMER_ATTRIBUTE_OPTIONS.filter(({ value }) =>
        selectedAttributes.includes(value),
      ).map(({ csvColumn, valuePattern, formatHint }) => ({
        name: csvColumn,
        valuePattern,
        formatHint,
      })),
    ],
  };
};

/** The header of a file that would pass `csvSpecForActions(actions)`. */
export const csvTemplateHeader = (csvSpec) =>
  `${csvSpec.requiredColumns.map(({ name }) => name).join(',')}\n`;

/**
 * The closure an SF/FA security report may follow. fraud-rules-service refuses a
 * security report beside any other status, so the form offers it nowhere else.
 */
export const isSecurityReportClosure = (statusCode, reasonCode) =>
  statusCode === ACCOUNT_STATUS_CODE.CLOSED && reasonCode === 'V9';

/** What each macro sends, which is the same wording whichever tenant holds it. */
const ZENDESK_MACRO_LABELS = Object.freeze({
  DOCV: 'DocV Request',
  DOCV_WALLET: 'DocV Request + Digital Wallet Verbiage',
  DOCV_ADDRESS: 'DocV + Address Request',
  DOCV_SSC: 'DocV + SSC Request',
  DOCV_SSC_ADDRESS: 'DocV, SSC, and Address Request',
});

/**
 * The macros a bulk document request may send here. Ids are per Zendesk tenant, so
 * they come from the environment — offering one the tenant does not hold gets the
 * whole file as far as the render and no further.
 */
export const ZENDESK_MACRO_OPTIONS = Object.freeze(
  Object.entries(config.ZENDESK_MACRO_IDS).map(([macro, value]) => ({
    value,
    label: ZENDESK_MACRO_LABELS[macro],
  })),
);

/**
 * What an agent may apply to a whole file. Every pair here must also be in
 * fraud-rules-service's `bulk_status_config.json`, which is what actually
 * validates it — offering one it does not know turns into a rejected upload.
 */
const STATUSES = Object.freeze([
  {
    code: ACCOUNT_STATUS_CODE.CARD_WATCH,
    name: 'Card Watch',
    reasons: [
      { code: '06', name: 'Temporarily Lost Card' },
      { code: '10', name: 'Suspicious Transaction' },
      { code: '11', name: 'Suspected Test Merchant' },
      { code: '12', name: 'Suspected Counterfeit' },
      { code: '13', name: 'Suspected Internet Fraud' },
      { code: '14', name: 'Suspected Token Fraud' },
    ],
  },
  {
    code: ACCOUNT_STATUS_CODE.TIERED_WATCH,
    name: 'Tiered Watch',
    reasons: [
      { code: 'L4', name: 'TSYS Identified ATO' },
      { code: 'L5', name: 'TSYS Identified Fraud App' },
      { code: 'L7', name: 'Suspected Payment Fraud' },
      { code: 'L8', name: 'Suspected ATO' },
      { code: 'L9', name: 'Suspected Fraud App' },
    ],
  },
  {
    code: ACCOUNT_STATUS_CODE.CLOSED,
    name: 'Closed',
    reasons: [
      { code: 'AF', name: 'Closed Annual Fee' },
      { code: 'BP', name: 'Closed Bank Policy' },
      { code: 'CT', name: 'Change in Terms' },
      { code: 'DU', name: 'Closed Did Not Use' },
      { code: 'IR', name: 'Closed Interest Rate' },
      { code: 'V1', name: 'Voluntary Closed 1' },
      { code: 'V8', name: 'Account Takeover' },
      { code: 'V9', name: 'Application Fraud' },
    ],
  },
]);

export const STATUS_CODE_OPTIONS = Object.freeze(
  STATUSES.map(({ code, name }) => ({ value: code, label: name })),
);

export const reasonCodeOptions = (statusCode) => {
  const status = STATUSES.find(({ code }) => code === statusCode);
  return (status?.reasons ?? []).map(({ code, name }) => ({
    value: code,
    label: `${statusCode}-${code}: ${name}`,
  }));
};

export const HISTORY_PAGE_SIZE = 10;
export const ROWS_PAGE_SIZE = 25;

/**
 * Mirrors `MAX_PAGE_SIZE` in fraud-rules-service's `BulkUploadController`, which
 * clamps a larger request down to it rather than refusing.
 */
export const DOWNLOAD_PAGE_SIZE = 200;

export const UPLOAD_STATUS = Object.freeze({
  AWAITING_FILE: { label: 'Awaiting file', color: 'default' },
  IN_PROGRESS: { label: 'In progress', color: 'processing' },
  COMPLETED: { label: 'Completed', color: 'success' },
  PARTIAL_SUCCESS: { label: 'Partial success', color: 'warning' },
  FAILED: { label: 'Failed', color: 'error' },
  REJECTED: { label: 'Rejected', color: 'error' },
  EXPIRED: { label: 'Expired', color: 'default' },
});

/**
 * "Rejected" and "Error" answer the two questions fraud-rules-service separates in
 * `BulkRunVerdict`: a decision was made about the account and it was no, versus a
 * decision was never made. Only the second is worth re-running, and neither is a
 * warning — both left the account as it was.
 */
export const ROW_STATUS = Object.freeze({
  IN_FLIGHT: { label: 'In flight', color: 'processing' },
  COMPLETED: { label: 'Completed', color: 'success' },
  FAILED: { label: 'Rejected', color: 'error' },
  ERROR: { label: 'Error', color: 'error' },
});

export const TASK_OUTCOME = Object.freeze({
  NOT_RUN: { label: 'Not run', color: 'default' },
  IN_FLIGHT: { label: 'In flight', color: 'processing' },
  COMPLETED: { label: 'Completed', color: 'success' },
  FAILED: { label: 'Rejected', color: 'error' },
  ERROR: { label: 'Error', color: 'error' },
  SKIPPED_DRY_RUN: { label: 'Skipped', color: 'default' },
});

/**
 * Task ids as fraud-rules-service names them in `TaskId`. One task covers both
 * the DocV link and the Zendesk ticket, so its label names both actions.
 */
export const STEP_LABEL = Object.freeze({
  'apply-bulk-account-update-task': 'Apply Account status',
  'create-bulk-docv-ticket-task': 'Create DocV link and Zendesk ticket',
  'add-bulk-security-report-task': ACTION_LABEL[ACTION_TYPE.SECURITY_REPORT],
  'apply-bulk-customer-info-update-task':
    ACTION_LABEL[ACTION_TYPE.CUSTOMER_INFO_UPDATE],
});

export const asOptions = (byValue) =>
  Object.entries(byValue).map(([value, { label }]) => ({ value, label }));
